// Centralized Typography and Color Palette for all Charts and Graphs (Dashboards, Footprint, etc.)
// Referenced in components like Footprint.js, DashboardGlobalFootprint.js, HorizontalStackedBarChart.js, BarChartWithTabs.js

import { M3_BRAND } from "./m3Palette";

export const GRAPH_PALETTE = {
  // Typography used strictly for charts/tables to match standardized DataGrid cells
  font: {
    family: "Poppins, sans-serif",
    sizes: {
      standard: "14px",        // Default size for ticks, labels, legend items
      mainAxisTitle: "13px",   // X and Y main titles
      donutTotalBig: "36px",   // "total numeric value" in Donut Chart center
      donutTotalSmall: "16px", // "TOTAL" and "kg CO₂e" in Donut Chart center
      axisLabel: "16px",       // Scaled down text for generic sub-labels
    },
    weights: {
      standard: 400, // Standard cells/ticks (e.g., "Mining", "Transport")
      medium: 500,   // Standard headers / sub-headers
      axisLabel: 500, // 
      bold: 600,     // Only for Main Axis labels (e.g., "GWP [kg CO₂e]")
      heavy: 700     // Heavy emphasis (e.g. donut main number)
    }
  },

  // Add inside your GRAPH_PALETTE object
  ui: {
    card: {
      backgroundLight: "#ffffff",
      backgroundDark: "#0f172a",
      borderLight: "#e2e8f0",
      borderDark: "#1e293b",
      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)",
    },
    controls: {
      bgLight: "#f8fafc",
      bgDark: "rgba(255,255,255,0.05)",
      activeLight: "#ffffff",
      activeDark: "#1e293b",
    },
    pills: {
      radius: "9999px",
      shadow: "0 2px 4px rgba(0,0,0,0.04)",
    }
  },

  // Chart Axis Line settings
  axes: {
    strokeWidth: 2, // Used to make main axis lines explicitly bold in Recharts and ChartJS
    lightMode: {
      tick: "#333333",
      line: "#333333"
    },
    darkMode: {
      tick: "#ffffff",
      line: "#f1f5f9"
    }
  },

  // Standardized LCA Stage Colors (used everywhere including Grid Tags and Charts)
  lcaStages: {
    // Stage A: Product & Construction
    A1: "#ef4444", // Raw Material Acquisition -> Red
    A2: "#f59e0b", // Transport to Factory -> Orange
    A3: "#0ea5e9", // Manufacturing -> Blue
    A4: "#22c55e", // Transport to Site -> Green
    A5: "#22c55e", // Construction/Installation -> Green

    // Stage B: Use Stage
    B1: "#14b8a6", // Use -> Teal
    B2: "#14b8a6", // Maintenance -> Teal
    B3: "#14b8a6", // Repair -> Teal
    B4: "#14b8a6", // Replacement -> Teal
    B5: "#14b8a6", // Refurbishment -> Teal
    B6: "#14b8a6", // Operational Energy Use -> Teal
    B7: "#14b8a6", // Operational Water Use -> Teal

    // Stage C: End of Life
    C1: "#ec4899", // Deconstruction/Demolition -> Pink
    C2: "#ec4899", // Transport to EOL -> Pink
    C3: "#ec4899", // Waste Processing -> Pink
    C4: "#ec4899", // Disposal -> Pink

    // Stage D: Beyond System Boundaries
    D: "#8b5cf6",  // Reuse/Recovery/Recycling Potential -> Purple

    // Other
    Biogenic: "#8b5cf6", // Biogenic Carbon -> Purple
    Fallback: "#3b82f6", // Unknown/Default -> Blue
  },

  // Standardized Scope Colors (used in Supply Chain and Structure Dashboards)
  scopes: {
    Scope1: "#ef4444", // Red
    Scope2: "#f59e0b", // Orange
    Scope3: "#0ea5e9"  // Blue
  },

  // Circularity material flow colors (input sources and end-of-life destinations).
  // `light` / `dark` = segment fill per mode, `text` = value label drawn on top of the segment.
  circularityFlows: {
    recycled: { light: "#2f7fd8", dark: "#4a93e6", text: "#ffffff" },            // Blue
    reuse: { light: M3_BRAND.primary, dark: M3_BRAND.primary, text: "#ffffff" }, // Arivu green
    remanufactured: { light: "#8b7ee0", dark: "#9a8ee8", text: "#ffffff" },      // Purple
    biological: { light: "#8d6e4a", dark: "#a1805a", text: "#ffffff" },          // Brown
    energyRecovery: { light: "#f28c28", dark: "#f59a3e", text: "#1f1f1f" },      // Orange
    virgin: { light: "#f2c230", dark: "#e8b923", text: "#1f1f1f" },              // Yellow
    waste: { light: "#a3a3a3", dark: "#8a8a8a", text: "#1f1f1f" },               // Grey (landfill)
    arrow: { light: "#9e9e9e", dark: "#5f5f5f" },
    segmentGap: "4px",
    segmentRadius: "6px",
    barHeight: 40,
    minLabelPercent: 8, // Segments narrower than this show their value only in the tooltip
  },

  // Scatter / bubble chart sizing (radius in px)
  scatter: {
    bubbleRadius: { min: 6, max: 32, default: 10 },
  }
};

/**
 * Universal helper function to get color by LCA stage
 * Use this dynamically instead of hard-coding arrays!
 */
export const getGraphColorByStage = (stageStr) => {
  if (!stageStr) return GRAPH_PALETTE.lcaStages.Fallback;
  const s = stageStr.toUpperCase().trim();

  if (s.startsWith("A1")) return GRAPH_PALETTE.lcaStages.A1;
  if (s.startsWith("A2")) return GRAPH_PALETTE.lcaStages.A2;
  if (s.startsWith("A3")) return GRAPH_PALETTE.lcaStages.A3;
  if (s.startsWith("A4") || s.startsWith("A5") || s === "PRODUCT" || s === "CONSTRUCTION") return GRAPH_PALETTE.lcaStages.A4;

  if (s.startsWith("B") || s.includes("USE") || s === "OPERATIONAL") return GRAPH_PALETTE.lcaStages.B1;
  if (s.startsWith("C") || s === "END OF LIFE" || s === "ENDOFLIFE") return GRAPH_PALETTE.lcaStages.C1;

  if (s.startsWith("D") || s.includes("AVOID") || s === "BENEFITS") return GRAPH_PALETTE.lcaStages.D;
  if (s.includes("BIOGENIC")) return GRAPH_PALETTE.lcaStages.Biogenic;

  return GRAPH_PALETTE.lcaStages.Fallback;
};
